// Tab Time Machine - Full CRUD with State Management
// Full history of tab states with restore capabilities

import React, { useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import {
  Clock, RotateCcw, GitBranch,
  ChevronRight, ChevronLeft, Bookmark,
  Trash2, Edit2, Save, X, Search, GitCompare, Copy
} from 'lucide-react';
import apiClient from '../services/api-client';
import { PageLayout } from '@/components/layout/PageLayout';
import { ConfirmDialog } from '../components/ui/Modal';
import { EmptyState } from '../components/ui/EmptyState';

interface TabChange {
  field: string;
  old_value?: unknown;
  new_value?: unknown;
  change_type: string;
}

interface TabSnapshot {
  id: string;
  tabId: string;
  versionNumber: number;
  changeType: string;
  snapshotType: 'manual' | 'scheduled' | 'triggered' | 'auto';
  url: string;
  title: string;
  favicon?: string;
  screenshot?: string;
  changes?: TabChange[];
  isFavorite: boolean;
  createdAt: string;
  updatedAt?: string;
  diffFromPrevious?: {
    url_changed: boolean;
    title_changed: boolean;
    group_changed: boolean;
    screenshot_changed?: boolean;
  };
  notes?: string;
}

interface TimeBookmark {
  id: string;
  name: string;
  description?: string;
  fromVersion: number;
  toVersion: number;
  tabId: string;
  color: string;
  createdAt: string;
}

interface CompareResult {
  added: string[];
  removed: string[];
  modified: string[];
}

const typeColors = {
  manual: { bg: 'bg-yellow-500/20', text: 'text-yellow-400', border: 'border-yellow-500' },
  scheduled: { bg: 'bg-blue-500/20', text: 'text-blue-400', border: 'border-blue-500' },
  triggered: { bg: 'bg-purple-500/20', text: 'text-purple-400', border: 'border-purple-500' },
  auto: { bg: 'bg-green-500/20', text: 'text-green-400', border: 'border-green-500' },
};

const changeTypeLabels: Record<string, string> = {
  create: 'Tab opened',
  update: 'Page changed',
  delete: 'Tab closed',
  restore: 'Restored',
  manual: 'Manual snapshot',
  checkpoint: 'Checkpoint',
};

function describeChangeType(changeType: string): string {
  const key = (changeType || 'update').toLowerCase();
  return changeTypeLabels[key] ?? changeType;
}

function describeSnapshotType(type: string): string {
  switch (type) {
    case 'manual':
      return 'Manual';
    case 'scheduled':
      return 'Scheduled';
    case 'triggered':
      return 'Event';
    default:
      return 'Automatic';
  }
}

const bookmarkColors = ['#8B5CF6', '#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#EC4899'];

function isDataImageUrl(src?: string | null): boolean {
  return Boolean(src && src.startsWith('data:image/'));
}

function normalizeSnapshot(raw: Record<string, unknown>): TabSnapshot {
  const snapshotType = (raw.snapshotType ?? raw.snapshot_type ?? raw.type ?? 'auto') as TabSnapshot['snapshotType'];
  const createdAt =
    (raw.createdAt as string) ??
    (raw.created_at as string) ??
    new Date().toISOString();
  const diffRaw = (raw.diffFromPrevious ?? raw.diff_from_previous) as TabSnapshot['diffFromPrevious'];
  const changes = (raw.changes as TabChange[] | undefined) ?? [];

  const changeType = String(raw.changeType ?? raw.change_type ?? snapshotType);

  return {
    id: String(raw.id ?? raw.snapshot_id ?? `snapshot-${Date.now()}`),
    tabId: String(raw.tabId ?? raw.tab_id ?? 'all'),
    versionNumber: Number(raw.versionNumber ?? raw.version ?? raw.version_number ?? 1),
    changeType,
    snapshotType,
    url: String(raw.url ?? ''),
    title: String(raw.title ?? 'Untitled'),
    favicon: raw.favicon as string | undefined,
    screenshot: (raw.screenshot ?? raw.viewport) as string | undefined,
    changes,
    isFavorite: Boolean(raw.isFavorite ?? raw.is_favorite ?? false),
    createdAt,
    updatedAt: (raw.updatedAt ?? raw.updated_at) as string | undefined,
    diffFromPrevious: diffRaw ?? {
      url_changed: changes.some((c) => c.field === 'url'),
      title_changed: changes.some((c) => c.field === 'title'),
      group_changed: changes.some((c) => c.field === 'favicon'),
      screenshot_changed: changes.some((c) => c.field === 'screenshot'),
    },
    notes: raw.notes as string | undefined,
  };
}

function normalizeBookmark(raw: Record<string, unknown>): TimeBookmark {
  return {
    id: String(raw.id ?? raw.bookmark_id ?? `bookmark-${Date.now()}`),
    name: String(raw.name ?? raw.label ?? 'Bookmark'),
    description: raw.description as string | undefined,
    fromVersion: Number(raw.fromVersion ?? raw.from_version ?? 1),
    toVersion: Number(raw.toVersion ?? raw.to_version ?? 1),
    tabId: String(raw.tabId ?? raw.tab_id ?? 'all'),
    color: String(raw.color ?? bookmarkColors[0]),
    createdAt: (raw.createdAt as string) ?? (raw.created_at as string) ?? new Date().toISOString(),
  };
}

export default function TabTimeMachinePage() {
  // State management
  const [searchParams, setSearchParams] = useSearchParams();
  const initialQ = searchParams.get('q') ?? '';
  const initialTabId = searchParams.get('tab') ?? undefined;
  const [snapshots, setSnapshots] = useState<TabSnapshot[]>([]);
  const [bookmarks, setBookmarks] = useState<TimeBookmark[]>([]);
  const [selectedSnapshot, setSelectedSnapshot] = useState<TabSnapshot | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [filterType, setFilterType] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState(initialQ);
  const [activeTabIdFilter, setActiveTabIdFilter] = useState<string | undefined>(initialTabId);

  // Compare mode
  const [compareMode, setCompareMode] = useState(false);
  const [compareSnapshots, setCompareSnapshots] = useState<TabSnapshot[]>([]);

  // Modal states
  const [showCreateBookmarkModal, setShowCreateBookmarkModal] = useState(false);
  const [showEditBookmarkModal, setShowEditBookmarkModal] = useState(false);
  const [showDeleteSnapshotConfirm, setShowDeleteSnapshotConfirm] = useState<string | null>(null);
  const [showDeleteBookmarkConfirm, setShowDeleteBookmarkConfirm] = useState<string | null>(null);
  const [showRestoreConfirm, setShowRestoreConfirm] = useState<TabSnapshot | null>(null);
  const [restoring, setRestoring] = useState(false);
  const [selectedBookmark, setSelectedBookmark] = useState<TimeBookmark | null>(null);
  const [showCompareModal, setShowCompareModal] = useState(false);

  // Form states
  const [bookmarkFormData, setBookmarkFormData] = useState({
    name: '',
    description: '',
    fromVersion: 0,
    toVersion: 0,
    color: bookmarkColors[0],
  });

  // Sync `?q=` and `?tab=` so filters survive back/forward and reloads.
  useEffect(() => {
    const next = new URLSearchParams(searchParams);
    if (searchQuery) next.set('q', searchQuery);
    else next.delete('q');
    if (activeTabIdFilter) next.set('tab', activeTabIdFilter);
    else next.delete('tab');
    if (next.toString() === searchParams.toString()) return;
    setSearchParams(next, { replace: true });
  }, [searchQuery, activeTabIdFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch versions when the tab filter changes.
  useEffect(() => {
    fetchSnapshots(activeTabIdFilter);
    fetchBookmarks();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTabIdFilter]);

  const fetchSnapshots = async (tabId?: string) => {
    try {
      setIsLoading(true);
      setError(null);
      // When the user lands on /tabs?section=time-machine with no specific
      // tab, we still want to render *something* useful. We pass
      // `undefined` so the API returns all versions instead of faking
      // a single "tab-1" entry.
      const data = await apiClient.getVersions(tabId);
      setSnapshots(data.map((item) => normalizeSnapshot(item)));
    } catch (err: any) {
      const status = err?.response?.status;
      if (status === 404) {
        setError('Time Machine API is unavailable. Restart the backend and try again.');
      } else {
        setError(err.message || 'Failed to fetch snapshots');
      }
      setSnapshots([]);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchBookmarks = async () => {
    try {
      const data = await apiClient.getTimeBookmarks();
      setBookmarks(data.map((item) => normalizeBookmark(item)));
    } catch (err) {
      console.error('Error fetching bookmarks:', err);
      setBookmarks([]);
    }
  };

  // CRUD Operations
  const createBookmark = async () => {
    try {
      const created = await apiClient.createTimeBookmark({
        tab_id: activeTabIdFilter ?? 'all',
        name: bookmarkFormData.name,
        label: bookmarkFormData.name,
      });
      const newBookmark = normalizeBookmark(created as Record<string, unknown>);
      setBookmarks((prev) => [
        {
          ...newBookmark,
          description: bookmarkFormData.description,
          fromVersion: bookmarkFormData.fromVersion,
          toVersion: bookmarkFormData.toVersion,
          color: bookmarkFormData.color,
        },
        ...prev,
      ]);
      setShowCreateBookmarkModal(false);
      resetBookmarkForm();
      toast.success('Bookmark created');
    } catch (err: any) {
      setError(err.message || 'Failed to create bookmark');
    }
  };

  const updateBookmark = async (id: string) => {
    try {
      const updated: Partial<TimeBookmark> = {
        name: bookmarkFormData.name,
        description: bookmarkFormData.description,
        fromVersion: bookmarkFormData.fromVersion,
        toVersion: bookmarkFormData.toVersion,
        color: bookmarkFormData.color,
      };

      setBookmarks(prev => prev.map(b => b.id === id ? { ...b, ...updated } : b));
      setShowEditBookmarkModal(false);
      setSelectedBookmark(null);
      resetBookmarkForm();
      // TODO: wire when version-control bookmarks endpoint is exposed.
    } catch (err: any) {
      setError(err.message || 'Failed to update bookmark');
    }
  };

  const deleteBookmark = async (id: string) => {
    try {
      setBookmarks(prev => prev.filter(b => b.id !== id));
      setShowDeleteBookmarkConfirm(null);
      if (selectedBookmark?.id === id) {
        setSelectedBookmark(null);
      }
      // TODO: wire when version-control bookmarks endpoint is exposed.
    } catch (err: any) {
      setError(err.message || 'Failed to delete bookmark');
    }
  };

  const deleteSnapshot = async (id: string) => {
    try {
      setSnapshots(prev => prev.filter(s => s.id !== id));
      setShowDeleteSnapshotConfirm(null);
      if (selectedSnapshot?.id === id) {
        setSelectedSnapshot(null);
      }
      // TODO: wire when version-control delete endpoint is exposed.
    } catch (err: any) {
      setError(err.message || 'Failed to delete snapshot');
    }
  };

  const resetBookmarkForm = () => {
    setBookmarkFormData({
      name: '',
      description: '',
      fromVersion: snapshots[snapshots.length - 1]?.versionNumber || 1,
      toVersion: snapshots[0]?.versionNumber || 1,
      color: bookmarkColors[0],
    });
  };

  const handleRestore = (snapshot: TabSnapshot) => {
    setShowRestoreConfirm(snapshot);
  };

  const confirmRestore = async () => {
    if (!showRestoreConfirm) return;
    setRestoring(true);
    try {
      const result = await apiClient.restoreVersion(showRestoreConfirm.tabId, showRestoreConfirm.id);
      const message =
        (result as { message?: string })?.message ??
        `Restored version ${showRestoreConfirm.versionNumber}. Your browser tab should update shortly.`;
      toast.success(message);
      setShowRestoreConfirm(null);
      await fetchSnapshots(activeTabIdFilter);
    } catch (err: unknown) {
      const msg = (err as Error)?.message ?? 'Failed to restore version';
      toast.error(msg);
      setError(msg);
    } finally {
      setRestoring(false);
    }
  };

  const removeCompareSnapshot = (id: string) => {
    setCompareSnapshots(prev => prev.filter(s => s.id !== id));
  };

  const openEditBookmarkModal = (bookmark: TimeBookmark) => {
    setSelectedBookmark(bookmark);
    setBookmarkFormData({
      name: bookmark.name,
      description: bookmark.description || '',
      fromVersion: bookmark.fromVersion,
      toVersion: bookmark.toVersion,
      color: bookmark.color,
    });
    setShowEditBookmarkModal(true);
  };

  const toggleCompareMode = () => {
    if (compareMode) {
      setCompareSnapshots([]);
    }
    setCompareMode(!compareMode);
  };

  const handleCompareSelect = (snapshot: TabSnapshot) => {
    if (compareSnapshots.length < 2) {
      setCompareSnapshots(prev => [...prev, snapshot]);
    }
  };

  const getCompareResult = (): CompareResult => {
    if (compareSnapshots.length !== 2) return { added: [], removed: [], modified: [] };

    const [older, newer] = compareSnapshots[0].versionNumber < compareSnapshots[1].versionNumber
      ? [compareSnapshots[0], compareSnapshots[1]]
      : [compareSnapshots[1], compareSnapshots[0]];

    const result: CompareResult = { added: [], removed: [], modified: [] };

    if (newer.url !== older.url) result.modified.push(`URL: ${older.url} → ${newer.url}`);
    if (newer.title !== older.title) result.modified.push(`Title: ${older.title} → ${newer.title}`);
    if (newer.snapshotType !== older.snapshotType) result.modified.push(`Type: ${older.snapshotType} → ${newer.snapshotType}`);
    if (newer.screenshot !== older.screenshot) result.modified.push('Screenshot changed');

    return result;
  };

  // Filter snapshots
  const filteredSnapshots = useMemo(() => {
    return snapshots.filter(snapshot => {
      if (filterType && snapshot.snapshotType !== filterType) return false;
      if (activeTabIdFilter && snapshot.tabId !== activeTabIdFilter) return false;
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        const title = (snapshot.title ?? '').toLowerCase();
        const url = (snapshot.url ?? '').toLowerCase();
        const notes = snapshot.notes?.toLowerCase() ?? '';
        return (
          title.includes(query) ||
          url.includes(query) ||
          notes.includes(query)
        );
      }
      return true;
    });
  }, [snapshots, filterType, searchQuery, activeTabIdFilter]);

  // Distinct tab ids we have snapshots for; used to render a filter chip strip.
  const tabIds = useMemo(() => {
    const ids = new Set<string>();
    for (const s of snapshots) ids.add(s.tabId);
    return Array.from(ids);
  }, [snapshots]);

  const tabLabels = useMemo(() => {
    const map = new Map<string, string>();
    for (const s of snapshots) {
      if (!map.has(s.tabId)) {
        const label = (s.title || s.url || s.tabId).trim();
        map.set(s.tabId, label.length > 28 ? `${label.slice(0, 26)}…` : label);
      }
    }
    return map;
  }, [snapshots]);

  const snapshotsForBookmarks = useMemo(() => {
    if (!activeTabIdFilter) return snapshots;
    return snapshots.filter((s) => s.tabId === activeTabIdFilter);
  }, [snapshots, activeTabIdFilter]);

  // Group filtered snapshots by day for sticky section headers.
  const snapshotsByDay = useMemo(() => {
    const groups = new Map<string, TabSnapshot[]>();
    for (const s of filteredSnapshots) {
      const day = new Date(s.createdAt).toLocaleDateString();
      const arr = groups.get(day);
      if (arr) arr.push(s);
      else groups.set(day, [s]);
    }
    return Array.from(groups.entries());
  }, [filteredSnapshots]);

  // Export the current snapshot list as a JSON file.
  const handleExport = () => {
    try {
      const data = JSON.stringify(
        { exportedAt: new Date().toISOString(), snapshots: filteredSnapshots },
        null,
        2,
      );
      const blob = new Blob([data], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `tab-time-machine-${new Date().toISOString().slice(0, 10)}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      toast.success('Exported version history');
    } catch (e) {
      toast.error('Export failed');
    }
  };

  // Keyboard nav: j/k move through filtered snapshots, Enter opens the detail panel.
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null;
      if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA')) return;
      if (e.key === 'j') {
        setCurrentIndex(i => Math.min(filteredSnapshots.length - 1, i + 1));
      } else if (e.key === 'k') {
        setCurrentIndex(i => Math.max(0, i - 1));
      } else if (e.key === 'Enter') {
        const cur = filteredSnapshots[currentIndex];
        if (cur) setSelectedSnapshot(cur);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [filteredSnapshots, currentIndex]);

  const formatTime = (dateStr: string) => {
    const date = new Date(dateStr);
    if (Number.isNaN(date.getTime())) return dateStr;
    return date.toLocaleString();
  };

  const getTypeStyles = (type: string) => {
    return typeColors[type as keyof typeof typeColors] || typeColors.auto;
  };

  return (
    <PageLayout
      title="Tab Time Machine"
      description="Browse saved snapshots of your tabs and restore any earlier version in one click"
      icon={Clock}
    >
      {/* Header */}
      <div className="bg-khiimori-bg-secondary/80 backdrop-blur-xl border-b border-khiimori-border/50 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <button
                onClick={toggleCompareMode}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all ${
                  compareMode ? 'bg-purple-600 text-white' : 'bg-khiimori-bg-tertiary text-khiimori-text-secondary hover:bg-khiimori-bg-tertiary'
                }`}
              >
                <GitCompare className="w-4 h-4" />
                {compareMode ? 'Exit Compare' : 'Compare'}
              </button>
              <button
                onClick={() => { resetBookmarkForm(); setShowCreateBookmarkModal(true); }}
                className="flex items-center gap-2 px-4 py-2 bg-amber-600 hover:bg-amber-500 text-khiimori-text-primary rounded-lg transition-all"
              >
                <Bookmark className="w-4 h-4" />
                Add Bookmark
              </button>
            </div>
          </div>

          {/* Stats Bar */}
          <div className="grid grid-cols-4 gap-4">
            <div className="bg-khiimori-bg-tertiary/30 rounded-xl p-3 flex items-center justify-between">
              <span className="text-khiimori-text-muted text-sm">Versions</span>
              <span className="text-khiimori-text-primary font-bold">{snapshots.length}</span>
            </div>
            <div className="bg-khiimori-bg-tertiary/30 rounded-xl p-3 flex items-center justify-between">
              <span className="text-khiimori-text-muted text-sm">Tabs tracked</span>
              <span className="text-khiimori-text-primary font-bold">{tabIds.length}</span>
            </div>
            <div className="bg-khiimori-bg-tertiary/30 rounded-xl p-3 flex items-center justify-between">
              <span className="text-khiimori-text-muted text-sm">Bookmarks</span>
              <span className="text-khiimori-text-primary font-bold">{bookmarks.length}</span>
            </div>
            <div className="bg-khiimori-bg-tertiary/30 rounded-xl p-3 flex items-center justify-between">
              <span className="text-khiimori-text-muted text-sm">Oldest snapshot</span>
              <span className="text-khiimori-text-primary font-bold text-sm">
                {snapshots.length > 0
                  ? new Date(snapshots[snapshots.length - 1]?.createdAt || Date.now()).toLocaleDateString()
                  : '—'}
              </span>
            </div>
          </div>

          {/* Filter & Search */}
          <div className="flex items-center gap-4 mt-4 flex-wrap">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-khiimori-text-muted text-sm">Filter:</span>
              {['auto', 'manual', 'triggered', 'scheduled'].map(type => (
                <button
                  key={type}
                  onClick={() => setFilterType(filterType === type ? null : type)}
                  className={`px-3 py-1 rounded-full text-xs transition-all ${
                    filterType === type ? 'bg-amber-600 text-white' : 'bg-khiimori-bg-tertiary text-khiimori-text-secondary hover:bg-khiimori-bg-tertiary'
                  }`}
                >
                  {describeSnapshotType(type)}
                </button>
              ))}
            </div>

            <div className="ml-auto relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-khiimori-text-muted" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search versions..."
                aria-label="Search versions"
                className="pl-10 pr-4 py-2 bg-khiimori-bg-tertiary/80 border border-slate-600/50 rounded-lg text-khiimori-text-primary placeholder-slate-400 focus:outline-none focus:border-amber-500/50 w-64"
              />
            </div>
          </div>

          {/* Tab-id filter chip strip */}
          {tabIds.length > 0 && (
            <div className="flex items-center gap-2 mt-3 overflow-x-auto no-scrollbar">
              <span className="text-khiimori-text-muted text-xs uppercase tracking-wider">Tab</span>
              <button
                onClick={() => setActiveTabIdFilter(undefined)}
                className={`px-2 py-1 rounded-full text-xs transition-all ${
                  !activeTabIdFilter
                    ? 'bg-amber-600 text-white'
                    : 'bg-khiimori-bg-tertiary text-khiimori-text-secondary hover:bg-khiimori-bg-tertiary'
                }`}
              >
                All
              </button>
              {tabIds.map(id => (
                <button
                  key={id}
                  onClick={() => setActiveTabIdFilter(id)}
                  className={`px-2 py-1 rounded-full text-xs transition-all max-w-[180px] truncate ${
                    activeTabIdFilter === id
                      ? 'bg-amber-600 text-white'
                      : 'bg-khiimori-bg-tertiary text-khiimori-text-secondary hover:bg-khiimori-bg-tertiary'
                  }`}
                  title={tabLabels.get(id) ?? id}
                >
                  {tabLabels.get(id) ?? id}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-6 py-6">
        <div className="mb-6 p-4 bg-amber-500/10 border border-amber-500/30 rounded-xl text-sm text-khiimori-text-secondary">
          <strong className="text-amber-300">How it works:</strong> Khiimori saves a snapshot when you open a tab,
          navigate to a new page, or when a screenshot is captured. Choose any version and click{' '}
          <strong className="text-khiimori-text-primary">Restore</strong> to rewind that tab&apos;s URL and title in
          both your workspace and Chrome (via the extension).
        </div>
        {error && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-6 p-4 bg-rose-500/20 border border-rose-500/50 rounded-xl text-rose-400 flex items-center justify-between"
          >
            <span>{error}</span>
            <button onClick={() => setError(null)} className="p-1 hover:bg-rose-500/30 rounded">
              <X className="w-4 h-4" />
            </button>
          </motion.div>
        )}

        <div className="grid grid-cols-3 gap-6">
          {/* Main Timeline */}
          <div className="col-span-2 space-y-6">
            {/* Timeline Navigation */}
            <div className="bg-khiimori-bg-secondary/80 backdrop-blur-xl rounded-2xl p-4 border border-khiimori-border/50">
              <div className="flex items-center justify-between">
                <button
                  onClick={() => setCurrentIndex(Math.max(0, currentIndex - 1))}
                  disabled={currentIndex === 0 || filteredSnapshots.length === 0}
                  className="p-2 bg-khiimori-bg-tertiary hover:bg-khiimori-bg-tertiary rounded-lg transition-all disabled:opacity-50"
                >
                  <ChevronLeft className="w-5 h-5 text-khiimori-text-primary" />
                </button>
                <div className="flex items-center gap-4">
                  <span className="text-khiimori-text-muted">Version</span>
                  <span className="text-2xl font-bold text-khiimori-text-primary">
                    {filteredSnapshots[currentIndex]?.versionNumber || 0}
                  </span>
                  <span className="text-khiimori-text-muted">of {filteredSnapshots.length}</span>
                </div>
                <button
                  onClick={() => setCurrentIndex(Math.min(filteredSnapshots.length - 1, currentIndex + 1))}
                  disabled={currentIndex >= filteredSnapshots.length - 1 || filteredSnapshots.length === 0}
                  className="p-2 bg-khiimori-bg-tertiary hover:bg-khiimori-bg-tertiary rounded-lg transition-all disabled:opacity-50"
                >
                  <ChevronRight className="w-5 h-5 text-khiimori-text-primary" />
                </button>
              </div>
              {/* Timeline scrubber */}
              <div className="mt-4 relative h-2 bg-khiimori-bg-tertiary rounded-full">
                <motion.div
                  className="absolute top-0 left-0 h-full bg-gradient-to-r from-amber-500 to-orange-500 rounded-full"
                  style={{ width: `${((currentIndex + 1) / Math.max(filteredSnapshots.length, 1)) * 100}%` }}
                />
              </div>
            </div>

            {/* Version List */}
            <div className="bg-khiimori-bg-secondary/80 backdrop-blur-xl rounded-2xl border border-khiimori-border/50 overflow-hidden">
              <div className="p-4 border-b border-khiimori-border/50 flex items-center justify-between">
                <h2 className="text-khiimori-text-primary font-semibold">Version History</h2>
                <span className="text-khiimori-text-muted text-sm">{filteredSnapshots.length} versions</span>
              </div>
              <div className="divide-y divide-khiimori-border/50 max-h-[500px] overflow-y-auto">
                {!isLoading && filteredSnapshots.length === 0 && (
                  <div className="p-8">
                    <EmptyState
                      variant="default"
                      title={searchQuery ? `No versions match "${searchQuery}"` : 'No history yet'}
                      description={
                        searchQuery
                          ? 'Try a different search or clear the active filter.'
                          : 'Open tabs in Chrome with the Khiimori extension enabled. Snapshots appear after navigation or screenshot capture.'
                      }
                    />
                  </div>
                )}
                {snapshotsByDay.map(([day, daySnaps]) => (
                  <React.Fragment key={day}>
                    <div className="sticky top-0 z-10 px-4 py-2 bg-khiimori-bg-secondary/95 backdrop-blur-sm text-xs text-khiimori-text-muted uppercase tracking-wider border-b border-khiimori-border/30">
                      {day}
                    </div>
                    {daySnaps.map((snapshot, idx) => {
                      const flatIdx = filteredSnapshots.findIndex(s => s.id === snapshot.id);
                      // suppress unused warning for idx; we use flatIdx below
                      void idx;
                  const typeStyles = getTypeStyles(snapshot.snapshotType);
                  const isCompareSelected = compareSnapshots.some(s => s.id === snapshot.id);

                  return (
                    <motion.div
                      key={snapshot.id}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: Math.min(0.2, flatIdx * 0.03) }}
                      className={`p-4 hover:bg-khiimori-bg-tertiary/30 cursor-pointer transition-all ${
                        currentIndex === flatIdx ? 'bg-khiimori-bg-tertiary/80 border-l-4 border-amber-500' : ''
                      } ${isCompareSelected ? 'bg-purple-500/20 border-l-4 border-purple-500' : ''}`}
                      onClick={() => {
                        if (compareMode) {
                          handleCompareSelect(snapshot);
                        } else {
                          setCurrentIndex(flatIdx);
                          setSelectedSnapshot(snapshot);
                        }
                      }}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div className="relative">
                            <div className={`w-10 h-10 ${typeStyles.bg} rounded-lg flex items-center justify-center`}>
                              <span className={`${typeStyles.text} font-bold`}>v{snapshot.versionNumber}</span>
                            </div>
                            {idx < filteredSnapshots.length - 1 && (
                              <div className="absolute top-full left-1/2 -translate-x-1/2 w-0.5 h-4 bg-khiimori-bg-tertiary" />
                            )}
                          </div>
                          <div>
                            <h3 className="text-khiimori-text-primary font-medium">{snapshot.title}</h3>
                            <p className="text-khiimori-text-muted text-sm truncate max-w-[300px]">{snapshot.url}</p>
                            <div className="flex items-center gap-2 mt-1">
                              <span className={`px-2 py-0.5 rounded text-xs ${typeStyles.bg} ${typeStyles.text}`}>
                                {describeChangeType(snapshot.changeType)}
                              </span>
                              {snapshot.diffFromPrevious?.url_changed && (
                                <span className="px-2 py-0.5 bg-red-500/20 text-red-400 rounded text-xs">URL changed</span>
                              )}
                              {snapshot.diffFromPrevious?.title_changed && (
                                <span className="px-2 py-0.5 bg-blue-500/20 text-blue-400 rounded text-xs">Title changed</span>
                              )}
                              {snapshot.diffFromPrevious?.screenshot_changed && (
                                <span className="px-2 py-0.5 bg-emerald-500/20 text-emerald-400 rounded text-xs">Screenshot changed</span>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <span className="text-khiimori-text-muted text-sm">{formatTime(snapshot.createdAt)}</span>
                          {!compareMode && (
                            <button
                              onClick={(e) => { e.stopPropagation(); handleRestore(snapshot); }}
                              className="p-2 bg-amber-600 hover:bg-amber-500 rounded-lg transition-all"
                              title="Restore this version"
                              aria-label={`Restore version ${snapshot.versionNumber}`}
                            >
                              <RotateCcw className="w-4 h-4 text-khiimori-text-primary" />
                            </button>
                          )}
                        </div>
                      </div>
                    </motion.div>
                  );
                    })}
                  </React.Fragment>
                ))}
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Version Detail */}
            {selectedSnapshot && !compareMode && (
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                className="bg-khiimori-bg-secondary/80 backdrop-blur-xl rounded-2xl p-6 border border-khiimori-border/50"
              >
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-khiimori-text-primary font-semibold">Version {selectedSnapshot.versionNumber} Details</h3>
                  <button onClick={() => setSelectedSnapshot(null)} className="p-2 hover:bg-khiimori-bg-tertiary rounded-lg">
                    <X className="w-4 h-4 text-khiimori-text-muted" />
                  </button>
                </div>
                <div className="space-y-4">
                  <div>
                    <span className="text-khiimori-text-muted text-sm">Title</span>
                    <p className="text-khiimori-text-primary">{selectedSnapshot.title}</p>
                  </div>
                  <div>
                    <span className="text-khiimori-text-muted text-sm">URL</span>
                    <div className="flex items-center gap-2">
                      <p className="text-khiimori-text-secondary text-sm break-all flex-1">{selectedSnapshot.url}</p>
                      <button
                        onClick={() => navigator.clipboard.writeText(selectedSnapshot.url)}
                        className="p-1 hover:bg-khiimori-bg-tertiary rounded"
                      >
                        <Copy className="w-4 h-4 text-khiimori-text-muted" />
                      </button>
                    </div>
                  </div>
                  <div>
                    <span className="text-khiimori-text-muted text-sm">Event</span>
                    <span className={`inline-block px-2 py-1 rounded text-xs ${getTypeStyles(selectedSnapshot.snapshotType).bg} ${getTypeStyles(selectedSnapshot.snapshotType).text}`}>
                      {describeChangeType(selectedSnapshot.changeType)}
                    </span>
                  </div>
                  <div>
                    <span className="text-khiimori-text-muted text-sm">Created</span>
                    <p className="text-khiimori-text-primary">{new Date(selectedSnapshot.createdAt).toLocaleString()}</p>
                  </div>
                  {selectedSnapshot.changes && selectedSnapshot.changes.length > 0 && (
                    <div>
                      <span className="text-khiimori-text-muted text-sm">Changes</span>
                      <ul className="mt-1 space-y-1">
                        {selectedSnapshot.changes.map((change, idx) => (
                          <li key={`${change.field}-${idx}`} className="text-xs text-khiimori-text-secondary">
                            <span className="text-khiimori-text-primary">{change.field}</span>
                            {change.old_value != null && change.new_value != null
                              ? `: ${String(change.old_value)} → ${String(change.new_value)}`
                              : change.new_value != null
                                ? `: ${String(change.new_value)}`
                                : ''}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {isDataImageUrl(selectedSnapshot.screenshot) && (
                    <div>
                      <span className="text-khiimori-text-muted text-sm">Screenshot</span>
                      <div className="mt-2 rounded-lg border border-khiimori-border/30 overflow-hidden bg-khiimori-bg-tertiary/30">
                        <img
                          src={selectedSnapshot.screenshot}
                          alt={`Version ${selectedSnapshot.versionNumber} preview`}
                          className="w-full h-auto max-h-56 object-cover object-top"
                        />
                      </div>
                    </div>
                  )}
                  {selectedSnapshot.notes && (
                    <div>
                      <span className="text-khiimori-text-muted text-sm">Notes</span>
                      <p className="text-khiimori-text-secondary text-sm">{selectedSnapshot.notes}</p>
                    </div>
                  )}
                </div>
                <button
                  onClick={() => handleRestore(selectedSnapshot)}
                  className="w-full mt-4 flex items-center justify-center gap-2 px-4 py-3 bg-gradient-to-r from-amber-600 to-orange-600 text-white rounded-xl hover:from-amber-500 hover:to-orange-500 transition-all"
                >
                  <RotateCcw className="w-5 h-5" />
                  Restore This Version
                </button>
              </motion.div>
            )}

            {/* Compare Mode Panel */}
            {compareMode && (
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                className="bg-khiimori-bg-secondary/80 backdrop-blur-xl rounded-2xl p-6 border border-purple-500/30"
              >
                <h3 className="text-khiimori-text-primary font-semibold mb-4">Compare Mode</h3>
                <p className="text-khiimori-text-muted text-sm mb-4">
                  Select 2 versions to compare. Selected: {compareSnapshots.length}/2
                </p>
                {compareSnapshots.length > 0 && (
                  <div className="space-y-2 mb-4">
                    {compareSnapshots.map(s => (
                      <div
                        key={s.id}
                        className="flex items-center justify-between px-3 py-1.5 bg-khiimori-bg-tertiary/50 rounded-lg text-sm"
                      >
                        <span className="text-khiimori-text-primary">
                          v{s.versionNumber} · {s.title}
                        </span>
                        <button
                          onClick={() => removeCompareSnapshot(s.id)}
                          className="p-1 hover:bg-rose-500/20 rounded"
                          aria-label={`Remove v${s.versionNumber} from compare`}
                        >
                          <X className="w-3 h-3 text-rose-400" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                {compareSnapshots.length === 2 ? (
                  <button
                    onClick={() => setShowCompareModal(true)}
                    className="w-full px-4 py-3 bg-purple-600 text-khiimori-text-primary rounded-xl hover:bg-purple-500 transition-all"
                  >
                    View Comparison
                  </button>
                ) : (
                  <p className="text-xs text-khiimori-text-muted">
                    Click two versions in the timeline to compare them.
                  </p>
                )}
              </motion.div>
            )}

            {/* Bookmarks */}
            <div className="bg-khiimori-bg-secondary/80 backdrop-blur-xl rounded-2xl p-6 border border-khiimori-border/50">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <Bookmark className="w-5 h-5 text-purple-400" />
                  <h3 className="text-khiimori-text-primary font-semibold">Time Bookmarks</h3>
                </div>
              </div>
              <div className="space-y-3">
                {bookmarks.map(bookmark => (
                  <div
                    key={bookmark.id}
                    className="p-4 bg-khiimori-bg-tertiary/30 rounded-xl cursor-pointer hover:bg-khiimori-bg-tertiary/80 transition-all"
                    onClick={() => {
                      const scope = bookmark.tabId && bookmark.tabId !== 'all'
                        ? snapshots.filter((s) => s.tabId === bookmark.tabId)
                        : snapshotsForBookmarks;
                      const fromIdx = scope.findIndex((s) => s.versionNumber === bookmark.fromVersion);
                      if (fromIdx !== -1) {
                        const target = scope[fromIdx];
                        const flatIdx = filteredSnapshots.findIndex((s) => s.id === target.id);
                        if (flatIdx !== -1) setCurrentIndex(flatIdx);
                        setSelectedSnapshot(target);
                        if (bookmark.tabId && bookmark.tabId !== 'all') {
                          setActiveTabIdFilter(bookmark.tabId);
                        }
                      }
                    }}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full" style={{ backgroundColor: bookmark.color }} />
                        <h4 className="text-khiimori-text-primary font-medium">{bookmark.name}</h4>
                      </div>
                      <div className="flex items-center gap-1">
                        <button
                          onClick={(e) => { e.stopPropagation(); openEditBookmarkModal(bookmark); }}
                          className="p-1 hover:bg-khiimori-bg-tertiary rounded"
                        >
                          <Edit2 className="w-3 h-3 text-khiimori-text-muted" />
                        </button>
                        <button
                          onClick={(e) => { e.stopPropagation(); setShowDeleteBookmarkConfirm(bookmark.id); setSelectedBookmark(bookmark); }}
                          className="p-1 hover:bg-rose-500/20 rounded"
                        >
                          <Trash2 className="w-3 h-3 text-rose-400" />
                        </button>
                      </div>
                    </div>
                    {bookmark.description && (
                      <p className="text-khiimori-text-muted text-sm mt-1">{bookmark.description}</p>
                    )}
                    <div className="flex items-center gap-4 mt-2 text-khiimori-text-muted text-sm">
                      <span>v{bookmark.fromVersion}</span>
                      <ChevronRight className="w-4 h-4" />
                      <span>v{bookmark.toVersion}</span>
                    </div>
                  </div>
                ))}
                {bookmarks.length === 0 && (
                  <p className="text-khiimori-text-muted text-center py-4">No bookmarks yet</p>
                )}
              </div>
            </div>

            {/* Quick Actions */}
            <div className="bg-khiimori-bg-secondary/80 backdrop-blur-xl rounded-2xl p-6 border border-khiimori-border/50">
              <h3 className="text-khiimori-text-primary font-semibold mb-4">Quick Actions</h3>
              <div className="space-y-2">
                <button
                  onClick={handleExport}
                  className="w-full p-3 bg-khiimori-bg-tertiary/80 hover:bg-khiimori-bg-tertiary rounded-xl text-left text-khiimori-text-secondary hover:text-khiimori-text-primary transition-all flex items-center gap-3"
                >
                  <GitBranch className="w-4 h-4" />
                  Export Version History
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Create Bookmark Modal */}
      <AnimatePresence>
        {showCreateBookmarkModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 modal-backdrop flex items-center justify-center z-50 p-4"
            onClick={() => setShowCreateBookmarkModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="modal-panel p-6 w-full max-w-md"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-khiimori-text-primary">Create Bookmark</h2>
                <button onClick={() => setShowCreateBookmarkModal(false)} className="p-2 hover:bg-khiimori-bg-tertiary rounded-lg">
                  <X className="w-5 h-5 text-khiimori-text-muted" />
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-khiimori-text-secondary text-sm mb-2">Name *</label>
                  <input
                    type="text"
                    value={bookmarkFormData.name}
                    onChange={(e) => setBookmarkFormData(prev => ({ ...prev, name: e.target.value }))}
                    placeholder="Bookmark name"
                    className="w-full px-4 py-2 bg-khiimori-bg-tertiary/80 border border-slate-600/50 rounded-lg text-khiimori-text-primary placeholder-slate-400 focus:outline-none focus:border-amber-500/50"
                  />
                </div>
                <div>
                  <label className="block text-khiimori-text-secondary text-sm mb-2">Description</label>
                  <textarea
                    value={bookmarkFormData.description}
                    onChange={(e) => setBookmarkFormData(prev => ({ ...prev, description: e.target.value }))}
                    placeholder="Optional description"
                    rows={2}
                    className="w-full px-4 py-2 bg-khiimori-bg-tertiary/80 border border-slate-600/50 rounded-lg text-khiimori-text-primary placeholder-slate-400 focus:outline-none focus:border-amber-500/50 resize-none"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-khiimori-text-secondary text-sm mb-2">From Version</label>
                    <select
                      value={bookmarkFormData.fromVersion}
                      onChange={(e) => setBookmarkFormData(prev => ({ ...prev, fromVersion: parseInt(e.target.value) }))}
                      className="w-full px-4 py-2 bg-khiimori-bg-tertiary/80 border border-slate-600/50 rounded-lg text-khiimori-text-primary focus:outline-none focus:border-amber-500/50"
                    >
                      {snapshotsForBookmarks.map(s => (
                        <option key={s.id} value={s.versionNumber}>v{s.versionNumber} · {s.title}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-khiimori-text-secondary text-sm mb-2">To Version</label>
                    <select
                      value={bookmarkFormData.toVersion}
                      onChange={(e) => setBookmarkFormData(prev => ({ ...prev, toVersion: parseInt(e.target.value) }))}
                      className="w-full px-4 py-2 bg-khiimori-bg-tertiary/80 border border-slate-600/50 rounded-lg text-khiimori-text-primary focus:outline-none focus:border-amber-500/50"
                    >
                      {snapshotsForBookmarks.map(s => (
                        <option key={s.id} value={s.versionNumber}>v{s.versionNumber} · {s.title}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <div>
                  <label className="block text-khiimori-text-secondary text-sm mb-2">Color</label>
                  <div className="flex gap-2">
                    {bookmarkColors.map(color => (
                      <button
                        key={color}
                        onClick={() => setBookmarkFormData(prev => ({ ...prev, color }))}
                        className={`w-8 h-8 rounded-full transition-all ${bookmarkFormData.color === color ? 'ring-2 ring-white ring-offset-2 ring-offset-slate-800' : ''}`}
                        style={{ backgroundColor: color }}
                      />
                    ))}
                  </div>
                </div>
              </div>

              <div className="flex gap-3 mt-6">
                <button
                  onClick={() => setShowCreateBookmarkModal(false)}
                  className="flex-1 px-4 py-2 bg-khiimori-bg-tertiary text-khiimori-text-secondary rounded-lg hover:bg-khiimori-bg-tertiary transition-all"
                >
                  Cancel
                </button>
                <button
                  onClick={createBookmark}
                  disabled={!bookmarkFormData.name}
                  className="flex-1 px-4 py-2 bg-amber-600 text-khiimori-text-primary rounded-lg hover:bg-amber-500 transition-all flex items-center justify-center gap-2 disabled:opacity-50"
                >
                  <Bookmark className="w-4 h-4" />
                  Create
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Edit Bookmark Modal */}
      <AnimatePresence>
        {showEditBookmarkModal && selectedBookmark && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 modal-backdrop flex items-center justify-center z-50 p-4"
            onClick={() => setShowEditBookmarkModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="modal-panel p-6 w-full max-w-md"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-khiimori-text-primary">Edit Bookmark</h2>
                <button onClick={() => setShowEditBookmarkModal(false)} className="p-2 hover:bg-khiimori-bg-tertiary rounded-lg">
                  <X className="w-5 h-5 text-khiimori-text-muted" />
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-khiimori-text-secondary text-sm mb-2">Name *</label>
                  <input
                    type="text"
                    value={bookmarkFormData.name}
                    onChange={(e) => setBookmarkFormData(prev => ({ ...prev, name: e.target.value }))}
                    className="w-full px-4 py-2 bg-khiimori-bg-tertiary/80 border border-slate-600/50 rounded-lg text-khiimori-text-primary focus:outline-none focus:border-amber-500/50"
                  />
                </div>
                <div>
                  <label className="block text-khiimori-text-secondary text-sm mb-2">Description</label>
                  <textarea
                    value={bookmarkFormData.description}
                    onChange={(e) => setBookmarkFormData(prev => ({ ...prev, description: e.target.value }))}
                    rows={2}
                    className="w-full px-4 py-2 bg-khiimori-bg-tertiary/80 border border-slate-600/50 rounded-lg text-khiimori-text-primary focus:outline-none focus:border-amber-500/50 resize-none"
                  />
                </div>
                <div>
                  <label className="block text-khiimori-text-secondary text-sm mb-2">Color</label>
                  <div className="flex gap-2">
                    {bookmarkColors.map(color => (
                      <button
                        key={color}
                        onClick={() => setBookmarkFormData(prev => ({ ...prev, color }))}
                        className={`w-8 h-8 rounded-full transition-all ${bookmarkFormData.color === color ? 'ring-2 ring-white ring-offset-2 ring-offset-slate-800' : ''}`}
                        style={{ backgroundColor: color }}
                      />
                    ))}
                  </div>
                </div>
              </div>

              <div className="flex gap-3 mt-6">
                <button
                  onClick={() => setShowEditBookmarkModal(false)}
                  className="flex-1 px-4 py-2 bg-khiimori-bg-tertiary text-khiimori-text-secondary rounded-lg hover:bg-khiimori-bg-tertiary transition-all"
                >
                  Cancel
                </button>
                <button
                  onClick={() => updateBookmark(selectedBookmark.id)}
                  className="flex-1 px-4 py-2 bg-amber-600 text-khiimori-text-primary rounded-lg hover:bg-amber-500 transition-all flex items-center justify-center gap-2"
                >
                  <Save className="w-4 h-4" />
                  Save
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Compare Modal */}
      <AnimatePresence>
        {showCompareModal && compareSnapshots.length === 2 && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 modal-backdrop flex items-center justify-center z-50 p-4"
            onClick={() => setShowCompareModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-khiimori-bg-secondary rounded-2xl p-6 w-full max-w-lg border border-khiimori-border"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-khiimori-text-primary">Version Comparison</h2>
                <button onClick={() => setShowCompareModal(false)} className="p-2 hover:bg-khiimori-bg-tertiary rounded-lg">
                  <X className="w-5 h-5 text-khiimori-text-muted" />
                </button>
              </div>

              <div className="grid grid-cols-2 gap-4 mb-6">
                {compareSnapshots.map((snapshot, idx) => (
                  <div key={snapshot.id} className="bg-khiimori-bg-tertiary/30 rounded-xl p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <span className={`px-2 py-0.5 rounded text-xs ${getTypeStyles(snapshot.snapshotType).bg} ${getTypeStyles(snapshot.snapshotType).text}`}>
                        v{snapshot.versionNumber}
                      </span>
                      <span className="text-khiimori-text-muted text-xs">
                        {idx === 0 ? 'Older' : 'Newer'}
                      </span>
                    </div>
                    <h4 className="text-khiimori-text-primary font-medium truncate">{snapshot.title}</h4>
                    <p className="text-khiimori-text-muted text-xs truncate">{snapshot.url}</p>
                  </div>
                ))}
              </div>

              <div className="space-y-4">
                <h4 className="text-khiimori-text-primary font-semibold">Changes</h4>
                {getCompareResult().modified.length > 0 ? (
                  <div className="space-y-2">
                    {getCompareResult().modified.map((change, idx) => (
                      <div key={idx} className="p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg">
                        <span className="text-amber-400 text-sm">{change}</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-khiimori-text-muted text-center py-4">No significant differences found</p>
                )}
              </div>

              <div className="flex gap-3 mt-6">
                <button
                  onClick={() => setShowCompareModal(false)}
                  className="flex-1 px-4 py-2 bg-khiimori-bg-tertiary text-khiimori-text-secondary rounded-lg hover:bg-khiimori-bg-tertiary transition-all"
                >
                  Close
                </button>
                <button
                  onClick={() => handleRestore(compareSnapshots[1])}
                  className="flex-1 px-4 py-2 bg-amber-600 text-khiimori-text-primary rounded-lg hover:bg-amber-500 transition-all"
                >
                  Restore Newer Version
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Delete Snapshot Confirmation */}
      <AnimatePresence>
        {showDeleteSnapshotConfirm && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 modal-backdrop flex items-center justify-center z-50 p-4"
            onClick={() => setShowDeleteSnapshotConfirm(null)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-khiimori-bg-secondary rounded-2xl p-6 w-full max-w-sm border border-khiimori-border"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="text-center">
                <div className="w-16 h-16 bg-rose-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Trash2 className="w-8 h-8 text-rose-400" />
                </div>
                <h3 className="text-xl font-bold text-khiimori-text-primary mb-2">Delete Snapshot?</h3>
                <p className="text-khiimori-text-muted mb-6">This action cannot be undone.</p>
                <div className="flex gap-3">
                  <button
                    onClick={() => setShowDeleteSnapshotConfirm(null)}
                    className="flex-1 px-4 py-2 bg-khiimori-bg-tertiary text-khiimori-text-secondary rounded-lg hover:bg-khiimori-bg-tertiary transition-all"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => deleteSnapshot(showDeleteSnapshotConfirm)}
                    className="flex-1 px-4 py-2 bg-rose-600 text-khiimori-text-primary rounded-lg hover:bg-rose-500 transition-all"
                  >
                    Delete
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Delete Bookmark Confirmation */}
      <AnimatePresence>
        {showDeleteBookmarkConfirm && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 modal-backdrop flex items-center justify-center z-50 p-4"
            onClick={() => setShowDeleteBookmarkConfirm(null)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-khiimori-bg-secondary rounded-2xl p-6 w-full max-w-sm border border-khiimori-border"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="text-center">
                <div className="w-16 h-16 bg-rose-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Trash2 className="w-8 h-8 text-rose-400" />
                </div>
                <h3 className="text-xl font-bold text-khiimori-text-primary mb-2">Delete Bookmark?</h3>
                <p className="text-khiimori-text-muted mb-6">This action cannot be undone.</p>
                <div className="flex gap-3">
                  <button
                    onClick={() => setShowDeleteBookmarkConfirm(null)}
                    className="flex-1 px-4 py-2 bg-khiimori-bg-tertiary text-khiimori-text-secondary rounded-lg hover:bg-khiimori-bg-tertiary transition-all"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => deleteBookmark(showDeleteBookmarkConfirm)}
                    className="flex-1 px-4 py-2 bg-rose-600 text-khiimori-text-primary rounded-lg hover:bg-rose-500 transition-all"
                  >
                    Delete
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Restore confirmation (uses the shared ConfirmDialog component). */}
      <ConfirmDialog
        isOpen={!!showRestoreConfirm}
        onClose={() => setShowRestoreConfirm(null)}
        onConfirm={confirmRestore}
        title="Restore this version?"
        message={
          showRestoreConfirm
            ? `Rewind "${showRestoreConfirm.title}" to version ${showRestoreConfirm.versionNumber} (${new Date(showRestoreConfirm.createdAt).toLocaleString()})? The tab will navigate to ${showRestoreConfirm.url}. A new "Restored" entry will be added to your history.`
            : ''
        }
        confirmText="Restore"
        cancelText="Cancel"
        variant="info"
        isLoading={restoring}
      />
    </PageLayout>
  );
}